/* ==========================================================================
   dewy — nav-drawer.js
   Mobile slide-out menu. Was copy-pasted identically into both pages; this
   is the single shared copy.

   Behaviour: open on burger click, close on overlay click / close button /
   any link / Escape. Locks body scroll while open, moves focus into the panel
   and keeps it there until the panel closes.

   WHY IT RE-BINDS. Editing the header in the theme editor makes Shopify
   replace the whole section, which throws away the burger and the drawer this
   file captured at load. Without re-binding, the listeners stay attached to
   detached nodes and the menu is dead for the rest of the editing session.
   `shopify:section:load` hands us the replaced section, so we re-arm only
   that one rather than re-scanning and double-binding the whole page.
   ========================================================================== */
(function () {
  'use strict';

  var CLOSE_MS = 260; // must match the .drawer-panel transition duration

  // Anything focusable inside the panel, in document order.
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]),' +
                  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function init(root) {
    var burger = root.querySelector('.nav-burger');
    var drawer = root.querySelector('#navdrawer') ||
                 document.getElementById('navdrawer');
    if (!burger || !drawer) return;

    // A second init on the same nodes would double every listener.
    if (burger.dataset.drawerBound) return;
    burger.dataset.drawerBound = '1';

    function open() {
      drawer.hidden = false;
      burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Next frame, so the browser registers the starting position before
      // .on flips it — otherwise the panel jumps instead of sliding.
      requestAnimationFrame(function () {
        drawer.classList.add('on');

        /* Focus has to wait for a SECOND frame, and the reason is worth
           keeping: .drawer is visibility:hidden until .on is applied
           (component-header.css), and .focus() on a visibility:hidden element
           does nothing whatsoever — silently, with no error and no thrown
           exception. Adding the class and focusing in the same frame is not
           enough, because the style has not been recomputed yet.

           When this was wrong, the drawer opened correctly and looked fine,
           but focus stayed on the burger — so the first three Tab presses
           walked through the nav links *behind* the overlay, which a keyboard
           user cannot see. The file claimed to manage focus and didn't. */
        requestAnimationFrame(function () {
          var first = drawer.querySelector('.drawer-close');
          if (first) first.focus();
        });
      });
    }

    function close() {
      drawer.classList.remove('on');
      burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      burger.focus();

      // Wait out the slide-out before hiding, so the transition is visible.
      setTimeout(function () {
        if (!drawer.classList.contains('on')) drawer.hidden = true;
      }, CLOSE_MS);
    }

    /* The panel declares role="dialog" aria-modal="true", which promises the
       rest of the page is unreachable. Without this, Tab walks straight out of
       the drawer and into the page underneath — which is completely covered by
       the overlay, so a keyboard user ends up operating controls they cannot
       see. Wrap focus around the panel's own controls instead. */
    function trapFocus(e) {
      if (e.key !== 'Tab' || !drawer.classList.contains('on')) return;

      var items = Array.prototype.filter.call(
        drawer.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!drawer.contains(document.activeElement)) {
        // Focus escaped some other way (a click outside, a restored state).
        e.preventDefault();
        first.focus();
      }
    }

    burger.addEventListener('click', open);

    drawer.addEventListener('click', function (e) {
      if (e.target === drawer ||
          e.target.closest('.drawer-close') ||
          e.target.closest('a')) {
        close();
      }
    });

    drawer.addEventListener('keydown', trapFocus);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('on')) close();
    });
  }

  function initAll() { init(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Only the replaced section, not the whole document.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
